import { useState, useRef, useCallback, useEffect } from "react";

export interface StepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  shiftStep?: number;
  disabled?: boolean;
  label?: string;
  suffix?: string;
  ariaLabel?: string;
}

export function Stepper({
  value,
  onChange,
  min,
  max,
  step = 1,
  shiftStep = 10,
  disabled = false,
  label,
  suffix,
  ariaLabel,
}: StepperProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const clamp = useCallback(
    (n: number): number => {
      let result = n;
      if (min !== undefined) result = Math.max(min, result);
      if (max !== undefined) result = Math.min(max, result);
      return result;
    },
    [min, max],
  );

  const commit = useCallback(
    (draftValue: string) => {
      const parsed = parseInt(draftValue, 10);
      const next = clamp(isNaN(parsed) ? value : parsed);
      setEditing(false);
      setDraft("");
      onChange(next);
    },
    [clamp, value, onChange],
  );

  const cancel = useCallback(() => {
    setEditing(false);
    setDraft("");
  }, []);

  const startEdit = useCallback(() => {
    cancelledRef.current = false;
    setDraft(String(value));
    setEditing(true);
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft(e.target.value.replace(/[^0-9-]/g, ""));
    },
    [],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit(draft);
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelledRef.current = true;
        cancel();
      }
    },
    [draft, commit, cancel],
  );

  const handleInputBlur = useCallback(() => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    commit(draft);
  }, [draft, commit]);

  const handleWrapperKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (editing) return;
      if (e.key === "ArrowUp") {
        e.preventDefault();
        onChange(clamp(value + (e.shiftKey ? shiftStep : step)));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        onChange(clamp(value - (e.shiftKey ? shiftStep : step)));
      }
    },
    [editing, value, clamp, step, shiftStep, onChange],
  );

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  const btnBase: React.CSSProperties = {
    width: "32px",
    border: "none",
    background: "transparent",
    color: "var(--paper-ink)",
    font: "500 18px sans-serif",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    flexShrink: 0,
  };

  return (
    <div style={rootStyle}>
      {label && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--paper-ink-2)",
            marginBottom: "4px",
          }}
        >
          {label}
        </div>
      )}
      <div
        role="spinbutton"
        aria-label={ariaLabel}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={editing ? -1 : 0}
        onKeyDown={handleWrapperKeyDown}
        style={{
          display: "inline-flex",
          alignItems: "stretch",
          border: "1px solid var(--paper-rule)",
          background: "var(--paper-bg)",
        }}
      >
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => onChange(clamp(value - step))}
          style={{
            ...btnBase,
            borderRight: "1px solid var(--paper-rule)",
          }}
        >
          {'\u2212'}
        </button>
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={draft}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            style={{
              font: "300 36px 'Fraunces', serif",
              padding: "6px 22px",
              minWidth: "90px",
              textAlign: "center",
              outline: "2px solid var(--paper-gold)",
              background: "var(--color-input)",
              border: "none",
              fontVariantNumeric: "tabular-nums",
              color: "var(--paper-ink)",
              width: "auto",
            }}
          />
        ) : (
          <div
            onClick={startEdit}
            style={{
              font: "300 36px 'Fraunces', serif",
              padding: "6px 22px",
              minWidth: "90px",
              textAlign: "center",
              cursor: "text",
              outline: "none",
              fontVariantNumeric: "tabular-nums",
              color: "var(--paper-ink)",
              userSelect: "none",
            }}
          >
            {value}
          </div>
        )}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => onChange(clamp(value + step))}
          style={{
            ...btnBase,
            borderLeft: "1px solid var(--paper-rule)",
          }}
        >
          +
        </button>
        {suffix && (
          <span
            style={{
              font: "500 12px 'JetBrains Mono', monospace",
              color: "var(--paper-ink-2)",
              alignSelf: "center",
              padding: "0 10px",
              whiteSpace: "nowrap",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}
